import {Form, FormField} from '@src/Api/Umbraco/interface/UmbracoTypes';
import {
  CONSENT_FIELD_COPY,
  CONSENT_FIELD_ALIASES,
  CONSENT_FIELD_DEFAULTS,
  DEFAULT_CONSENT_FIELDS,
  LEGACY_CONSENT_FIELD_ALIASES,
} from './consent.constants';

const consentAliasSet = new Set<string>(Object.values(CONSENT_FIELD_ALIASES));
const legacyConsentAliasSet = new Set<string>(
  Object.values(LEGACY_CONSENT_FIELD_ALIASES),
);

const legacyToCanonicalAlias: Record<string, string | undefined> = {
  [LEGACY_CONSENT_FIELD_ALIASES.terms]: CONSENT_FIELD_ALIASES.terms,
  [LEGACY_CONSENT_FIELD_ALIASES.marketing]:
    CONSENT_FIELD_ALIASES.traceMarketing,
};

const collectFieldAliases = (form: Form) => {
  const aliases = new Set<string>();

  form.pages.forEach(page => {
    page.fieldsets.forEach(fieldset => {
      fieldset.columns.forEach(column => {
        column.fields.forEach(field => aliases.add(field.alias));
      });
    });
  });

  return aliases;
};

const getCanonicalConsentField = (field: FormField): FormField | null => {
  const canonicalAlias = legacyToCanonicalAlias[field.alias] || field.alias;

  if (!consentAliasSet.has(canonicalAlias)) {
    return legacyConsentAliasSet.has(field.alias) ? null : field;
  }

  const copy =
    CONSENT_FIELD_COPY[canonicalAlias as keyof typeof CONSENT_FIELD_COPY];
  const isTerms = canonicalAlias === CONSENT_FIELD_ALIASES.terms;

  // Consent labels and aliases are canonicalized so Umbraco reflects the same legal model as the frontend.
  return {
    ...field,
    alias: canonicalAlias,
    caption: copy.caption,
    required: isTerms,
    requiredErrorMessage: isTerms
      ? field.requiredErrorMessage ||
        'You must accept the Terms and Conditions to continue'
      : undefined,
    type: isTerms ? 'dataConsent' : 'checkbox',
    settings: {
      ...field.settings,
      acceptCopy: copy.acceptCopy,
    },
  };
};

const normalizeConsentFields = (form: Form): Form => {
  const seenConsentAliases = new Set<string>();

  return {
    ...form,
    pages: form.pages.map(page => ({
      ...page,
      fieldsets: page.fieldsets.map(fieldset => ({
        ...fieldset,
        columns: fieldset.columns.map(column => ({
          ...column,
          fields: column.fields.reduce<FormField[]>((fields, field) => {
            const canonicalField = getCanonicalConsentField(field);
            if (!canonicalField) {
              return fields;
            }
            if (
              consentAliasSet.has(canonicalField.alias) &&
              seenConsentAliases.has(canonicalField.alias)
            ) {
              return fields;
            }
            if (consentAliasSet.has(canonicalField.alias)) {
              seenConsentAliases.add(canonicalField.alias);
            }
            return [...fields, canonicalField];
          }, []),
        })),
      })),
    })),
  };
};

export const withConsentFields = (form: Form): Form => {
  const normalizedForm = normalizeConsentFields(form);
  const existingAliases = collectFieldAliases(normalizedForm);
  const missingConsentFields = DEFAULT_CONSENT_FIELDS.filter(
    field => !existingAliases.has(field.alias),
  );

  if (!missingConsentFields.length) {
    return normalizedForm;
  }

  const lastPageIndex = normalizedForm.pages.length - 1;

  return {
    ...normalizedForm,
    pages: normalizedForm.pages.map((page, pageIndex) => {
      if (pageIndex !== lastPageIndex) {
        return page;
      }

      return {
        ...page,
        fieldsets: [
          ...page.fieldsets,
          {
            columns: [
              {
                width: 12,
                fields: missingConsentFields,
              },
            ],
          },
        ],
      };
    }),
  };
};

export const withConsentInitialValues = (
  initialValues: Record<string, any> = {},
) => ({
  ...CONSENT_FIELD_DEFAULTS,
  ...initialValues,
});
